import { Helmet } from 'react-helmet-async';
import { Form, Card, InputGroup } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Header1 from '../components/Header1';
import api from '../api';
import {
  FaUser,
  FaEnvelope,
  FaCommentDots,
  FaPhone,
  FaMapMarker,
  FaPaperPlane,
} from 'react-icons/fa';
import { IoBusiness } from 'react-icons/io5';
import Swal from 'sweetalert2';
import BackgroundAnimation from '../components/BackgroundAnimation';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState({
    email: 'office@kobithangarments.com',
    phone: '0703533536',
    address: 'No.14, Eight Acre, Viyasar Road, Thonikkal, Vavuniya, Sri Lanka. 43000',
    google_map_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3943.396988716825!2d80.47928217449642!3d8.748666293315214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3afc15120f00d845%3A0x86603b4272a0c401!2sKobithan%20Garments%20(Pvt)%20Ltd!5e0!3m2!1sen!2slk!4v1746559594433!5m2!1sen!2slk',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/api/settings');
        if (data) {
          // If google_map_url is a share link, it might need to be an embed link for the iframe
          // But for now, we'll use what's in the DB or fallback
          setSettings({
            ...data,
            // Simple check to ensure we use an embeddable URL in the iframe
            google_map_url: data.google_map_url && data.google_map_url.includes('embed') 
              ? data.google_map_url 
              : 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3943.396988716825!2d80.47928217449642!3d8.748666293315214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3afc15120f00d845%3A0x86603b4272a0c401!2sKobithan%20Garments%20(Pvt)%20Ltd!5e0!3m2!1sen!2slk!4v1746559594433!5m2!1sen!2slk'
          });
        }
      } catch (err) {
        console.error('Error fetching site settings', err);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data } = await api.post('/api/inquiries', formData);

      Swal.fire({
        title: 'Thank You!',
        text: 'Your message has been sent successfully.',
        icon: 'success',
        confirmButtonColor: '#801001',
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Network error. Please try again later.',
        icon: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    header: {
      fontSize: '2rem',
      color: '#801001',
    },
    iconCircle: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#801001',
      transition: 'transform 0.3s ease',
    },
    contactCard: {
      borderRadius: '15px',
      background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
      border: '2px solid #801001',
    },
    inputGroupText: {
      background: '#801001',
      borderColor: '#801001',
    },
    formControl: {
      borderColor: '#801001',
    },
    formControlFocus: {
      borderColor: '#801001',
      boxShadow: '0 0 0 0.25rem rgba(128, 16, 1, 0.25)',
    },
    submitButton: {
      background: '#801001',
      border: 'none',
      fontWeight: '600',
      borderRadius: '8px',
      transition: '0.3s',
      fontSize: '1.1rem',
    },
    submitButtonHover: {
      background: '#5c0b00',
    },
    contactInfoItem: {
      '&:hover $iconCircle': {
        transform: 'scale(1.1)',
      },
    },
  };

  return (
    <div>
      <Header1 />
      <BackgroundAnimation />
      <Container className="d-flex justify-content-center align-items-center mt-5">
        <Helmet>
          <title>Contact Us</title>
        </Helmet>

        <Row className="w-100" style={{ maxWidth: '900px' }}>
          <Col md={6} className="p-4">
            <h2 className="mb-4" style={styles.header}>
              <IoBusiness className="me-2" />
              Get in Touch
            </h2>
            <p className="lead mb-4">
              We'd love to hear from you! Feel free to reach out to us using the
              form.
            </p>

            <div
              className="contact-info-item mb-3"
              style={styles.contactInfoItem}
            >
              <div className="d-flex align-items-center mb-3">
                <div className="icon-circle me-3" style={styles.iconCircle}>
                  <FaPhone className="text-white p-2" size={32} />
                </div>
                <div>
                  <h5 style={{ color: '#801001' }}>Phone</h5>
                  <a
                    href={`tel:${settings.phone}`}
                    style={{ textDecoration: 'none', color: '#333' }}
                  >
                    <p className="mb-0 hover-underline">{settings.phone}</p>
                  </a>
                </div>
              </div>
            </div>

            <div
              className="contact-info-item mb-3"
              style={styles.contactInfoItem}
            >
              <div className="d-flex align-items-center mb-3">
                <div className="icon-circle me-3" style={styles.iconCircle}>
                  <FaEnvelope className="text-white p-2" size={32} />
                </div>
                <div>
                  <h5 style={{ color: '#801001' }}>Email</h5>
                  <a
                    href={`mailto:${settings.email}`}
                    style={{ textDecoration: 'none', color: '#333' }}
                  >
                    <p className="mb-0 hover-underline">
                      {settings.email}
                    </p>
                  </a>
                </div>
              </div>
            </div>

            <div
              className="contact-info-item mb-3"
              style={styles.contactInfoItem}
            >
              <div className="d-flex align-items-center mb-3">
                <div className="icon-circle me-3" style={styles.iconCircle}>
                  <FaMapMarker className="text-white p-2" size={32} />
                </div>
                <div>
                  <h5 style={{ color: '#801001' }}>Address</h5>
                  <p className="mb-0">
                    {settings.address}
                  </p>
                </div>
              </div>
            </div>
          </Col>

          {/* Right Side - Contact Form */}
          <Col md={6}>
            <Card className="shadow-lg p-4" style={styles.contactCard}>
              <Form onSubmit={handleSubmit}>
                <input
                  type="hidden"
                  name="_gotcha"
                  style={{ display: 'none' }}
                />
                <Form.Group className="mb-4" controlId="name">
                  <Form.Label className="fw-bold" style={{ color: '#801001' }}>
                    Your Name
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text style={styles.inputGroupText}>
                      <FaUser className="text-white" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      required
                      style={styles.formControl}
                      onFocus={(e) =>
                        (e.target.style = {
                          ...styles.formControl,
                          ...styles.formControlFocus,
                        })
                      }
                      onBlur={(e) => (e.target.style = styles.formControl)}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4" controlId="email">
                  <Form.Label className="fw-bold" style={{ color: '#801001' }}>
                    Email Address
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text style={styles.inputGroupText}>
                      <FaEnvelope className="text-white" />
                    </InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                      style={styles.formControl}
                      onFocus={(e) =>
                        (e.target.style = {
                          ...styles.formControl,
                          ...styles.formControlFocus,
                        })
                      }
                      onBlur={(e) => (e.target.style = styles.formControl)}
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4" controlId="message">
                  <Form.Label className="fw-bold" style={{ color: '#801001' }}>
                    Your Message
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text style={styles.inputGroupText}>
                      <FaCommentDots className="text-white" />
                    </InputGroup.Text>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Write your message..."
                      rows={4}
                      required
                      style={styles.formControl}
                      onFocus={(e) =>
                        (e.target.style = {
                          ...styles.formControl,
                          ...styles.formControlFocus,
                        })
                      }
                      onBlur={(e) => (e.target.style = styles.formControl)}
                    />
                  </InputGroup>
                </Form.Group>

                <div className="text-center mt-4">
                  <Button
                    type="submit"
                    className="w-100 py-2 d-flex justify-content-center align-items-center gap-2 position-relative overflow-hidden"
                    style={{
                      ...styles.submitButton,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background =
                        styles.submitButtonHover.background;
                      const icon = e.currentTarget.querySelector('.plane-icon');
                      if (icon) icon.style.transform = 'translateX(5px)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background =
                        styles.submitButton.background;
                      const icon = e.currentTarget.querySelector('.plane-icon');
                      if (icon) icon.style.transform = 'translateX(0)';
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <FaPaperPlane
                          className="plane-icon"
                          style={{
                            transition: 'transform 0.3s ease',
                          }}
                        />
                        <span className="fw-bold">Send Message</span>
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>

      <div className="mt-5">
        <h2 className="mb-4 text-center" style={styles.header}>
          Our Location
        </h2>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <iframe
            src={settings.google_map_url}
            width="100%"
            height="400"
            style={{ border: 0, maxWidth: '900px' }}
            title="Google Maps - Kobithan Garments Location"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}
